/**
 * Exam Preset Rubrics
 *
 * Complete rubric definitions for each exam board preset, including
 * dimension weights, band descriptors, and scoring bands.
 *
 * These are based on official marking schemes from each exam board.
 */

import type { EssayConfig, EssayBand } from "@/types/grading";

export interface ExamPresetRubric {
  value: string;
  label: string;
  category: string;
  description: string;
  gradeLevel?: string;
  essayConfig: EssayConfig;
}

/**
 * DSE 中文作文 - 香港中學文憑考試 中國語文科 寫作能力
 * Total: 103 points (內容40 + 表達30 + 結構20 + 標點字體10 + 錯別字3)
 * Based on 2025 HKDSE Chinese Language Writing Assessment criteria
 * Source: 2025年香港中學文憑考試 中國語文科教師會議 (CHI-BS-2025-3.pdf)
 */
const dseChineseRubric: ExamPresetRubric = {
  value: "dse_chinese",
  label: "DSE 中文作文",
  category: "DSE (香港)",
  description: "香港中學文憑考試 中國語文科 寫作能力",
  gradeLevel: "S6",
  essayConfig: {
    dimensions: {
      content: {
        name: "內容",
        weight: 39, // 40/103 ≈ 39%
        maxScore: 40,
        bandDescriptors: {
          excellent:
            "立意深刻新穎，內容充實豐富，合乎寫作要求；取材恰當，能彰顯立意；闡述合理、飽滿，思想感情真摯動人，見解獨到深刻",
          good: "立意明確，內容充實，合乎寫作要求；取材較為恰當，能彰顯立意；闡述較為合理、飽滿，思想感情真實，有一定見解",
          fair: "立意基本明確，內容尚算充實，基本合乎寫作要求；取材尚可，基本能彰顯立意；闡述基本合理，思想感情尚算真實",
          needsImprovement:
            "立意不明確或偏離題目，內容空泛或離題，不合乎寫作要求；取材不當，未能彰顯立意；闡述不合理，思想感情虛假",
        },
      },
      organization: {
        name: "結構",
        weight: 19, // 20/103 ≈ 19%
        maxScore: 20,
        bandDescriptors: {
          excellent:
            "段落區分明晰，起承轉合自然流暢，輕重詳略恰當，結構嚴謹完整，層次分明清晰，首尾呼應",
          good: "段落區分較為明晰，起承轉合較為自然，輕重詳略較為恰當，結構完整，層次較為分明",
          fair: "段落區分尚算明晰，起承轉合尚算自然，輕重詳略尚可，結構基本完整，層次尚算分明",
          needsImprovement:
            "段落區分不清，起承轉合生硬，輕重詳略不當，結構混亂，層次不清，缺乏連貫性",
        },
      },
      language: {
        name: "表達",
        weight: 29, // 30/103 ≈ 29%
        maxScore: 30,
        bandDescriptors: {
          excellent:
            "用詞精確、豐富，文句簡潔流暢，表達手法純熟靈活，句式靈活多變，善用修辭手法（比喻、排比、對偶等），文采斐然，表達生動有力",
          good: "用詞較為精確、豐富，文句較為簡潔流暢，表達手法較為純熟靈活，句式較為多變，能運用修辭手法，表達較為生動",
          fair: "用詞基本正確，文句基本通順，表達手法基本，句式較為單調，能運用簡單修辭，表達尚算清楚",
          needsImprovement:
            "用詞不精確、貧乏，文句不通順，表達手法生硬，句式單調，缺乏修辭運用，表達不清，文筆生硬",
        },
      },
      mechanics: {
        name: "標點字體及錯別字",
        weight: 13, // (10+3)/103 ≈ 13% - 標點字體10分 + 錯別字3分
        maxScore: 13, // 標點字體10分 + 錯別字3分（合併評分）
        bandDescriptors: {
          excellent:
            "【標點字體10分】字體工整美觀，標點符號使用準確且靈活（標點使用重在準確與靈活，而非數量多寡）。【錯別字3分】錯別字0-1個（3分）",
          good: "【標點字體10分】字體較為工整，標點符號使用基本準確且較為靈活。【錯別字3分】錯別字2-4個（2分）",
          fair: "【標點字體10分】字體尚可，標點符號使用尚算準確，偶有錯誤。【錯別字3分】錯別字5-7個（1分）",
          needsImprovement:
            "【標點字體10分】字體潦草，標點符號使用不準確或混亂。【錯別字3分】錯別字8個或以上（0分）",
        },
      },
    },
    bands: [
      {
        level: "優秀",
        scoreRange: [85, 103],
        description:
          "內容深刻充實，結構嚴謹完整，表達優美流暢，標點字體準確，錯別字極少，整體表現優秀",
      },
      {
        level: "良好",
        scoreRange: [70, 84],
        description:
          "內容充實，結構清晰完整，表達較為流暢，標點字體基本正確，錯別字少，整體表現良好",
      },
      {
        level: "尚可",
        scoreRange: [50, 69],
        description:
          "內容尚算充實，結構基本完整，表達尚算清楚，標點字體尚可，有少量錯別字，整體表現尚可",
      },
      {
        level: "待改進",
        scoreRange: [0, 49],
        description:
          "內容空泛或離題，結構混亂，表達不清，標點字體問題較多，錯別字較多，整體表現有待改進",
      },
    ],
    examPreset: "dse_chinese",
  },
};

/**
 * AQA GCSE - UK GCSE Chinese Language
 * Total: 40 points
 * Based on AQA GCSE marking criteria
 */
const aqaGcseRubric: ExamPresetRubric = {
  value: "aqa_gcse",
  label: "AQA GCSE",
  category: "GCSE (UK)",
  description: "AQA GCSE 中文",
  gradeLevel: "S4",
  essayConfig: {
    dimensions: {
      content: {
        name: "內容與創意",
        weight: 35,
        maxScore: 14,
        bandDescriptors: {
          excellent:
            "構思深刻新穎，內容充實豐富，能緊扣題目要求，理解寫作目的和讀者對象，內容引人入勝且具原創性",
          good: "構思清晰，內容充實，能回應題目要求，理解寫作目的和讀者對象，內容相關且恰當",
          fair: "構思基本清晰，內容部分充實，基本理解寫作目的和讀者對象，內容大致相關",
          needsImprovement:
            "構思簡單或有限，內容未充分發展，對寫作目的和讀者對象理解不足，內容可能不相關或不清楚",
        },
      },
      organization: {
        name: "組織結構",
        weight: 25,
        maxScore: 10,
        bandDescriptors: {
          excellent:
            "結構嚴謹，層次清晰，段落運用有效，連貫手法運用得當，文本結構控制嫻熟",
          good: "結構清晰，邏輯分明，段落運用良好，能運用連貫手法，整體組織良好",
          fair: "結構基本清晰，有基本層次，段落運用簡單，連貫手法有限，組織基本",
          needsImprovement:
            "結構有限或無結構，段落運用不佳，連貫手法很少，結構混亂或不清楚",
        },
      },
      language: {
        name: "詞彙與表達",
        weight: 25,
        maxScore: 10,
        bandDescriptors: {
          excellent: "詞彙豐富且精準，用詞準確，句式多變複雜，語言控制優秀",
          good: "詞彙多樣，用詞大多恰當，句式多樣，語言控制良好",
          fair: "詞彙簡單，用詞基本，句式簡單，語言控制基本",
          needsImprovement: "詞彙有限，用詞不當，句式有限，語言控制不佳",
        },
      },
      mechanics: {
        name: "字詞標點語法",
        weight: 15,
        maxScore: 6,
        bandDescriptors: {
          excellent: "字詞、標點和語法完全準確，語言規範運用嫻熟且準確",
          good: "字詞、標點和語法大多準確，語言規範運用良好",
          fair: "字詞、標點和語法部分準確，語言規範運用基本，有少量錯誤",
          needsImprovement: "字詞、標點和語法錯誤頻繁，語言規範準確性有限",
        },
      },
    },
    bands: [
      {
        level: "優秀",
        scoreRange: [34, 40],
        description: "構思深刻，組織優秀，詞彙豐富，字詞標點語法完全準確",
      },
      {
        level: "良好",
        scoreRange: [28, 33],
        description: "構思清晰，組織良好，詞彙多樣，字詞標點語法大多準確",
      },
      {
        level: "尚可",
        scoreRange: [20, 27],
        description: "構思基本清晰，組織基本，詞彙簡單，字詞標點語法有部分錯誤",
      },
      {
        level: "待改進",
        scoreRange: [0, 19],
        description: "構思有限，組織不佳，詞彙有限，字詞標點語法錯誤頻繁",
      },
    ],
    examPreset: "aqa_gcse",
  },
};

/**
 * Edexcel GCSE - Pearson Edexcel GCSE Chinese
 * Total: 40 points
 */
const edexcelGcseRubric: ExamPresetRubric = {
  value: "edexcel_gcse",
  label: "Edexcel GCSE",
  category: "GCSE (UK)",
  description: "Pearson Edexcel GCSE 中文",
  gradeLevel: "S4",
  essayConfig: {
    dimensions: {
      content: {
        name: "內容",
        weight: 40,
        maxScore: 16,
        bandDescriptors: {
          excellent:
            "完全回應題目要求，構思深刻新穎，內容引人入勝、具原創性且充分發展",
          good: "回應題目要求，構思清晰且充分發展，內容相關且恰當，大多充分發展",
          fair: "部分回應題目要求，構思基本清晰，內容大致相關但未充分發展",
          needsImprovement:
            "對題目要求回應有限或無回應，內容不相關、不清楚或未發展",
        },
      },
      organization: {
        name: "組織與連貫",
        weight: 20,
        maxScore: 8,
        bandDescriptors: {
          excellent:
            "結構優秀，邏輯清晰，段落運用有效，連貫手法運用得當，高度連貫",
          good: "結構良好，邏輯分明，段落運用恰當，能運用連貫手法，整體連貫",
          fair: "結構基本，有基本邏輯，段落運用簡單，連貫手法有限，部分連貫",
          needsImprovement:
            "結構不佳或無結構，段落運用不足，連貫手法很少，缺乏連貫性",
        },
      },
      language: {
        name: "語言範圍與準確性",
        weight: 30,
        maxScore: 12,
        bandDescriptors: {
          excellent: "詞彙和句式範圍廣泛，語言使用高度準確，表達複雜精緻",
          good: "詞彙和句式範圍良好，語言使用大多準確，表達清晰",
          fair: "詞彙和句式範圍有限，語言使用部分準確，表達基本",
          needsImprovement: "詞彙和句式範圍非常有限，錯誤頻繁，表達不佳",
        },
      },
      mechanics: {
        name: "準確性",
        weight: 10,
        maxScore: 4,
        bandDescriptors: {
          excellent: "字詞、標點和語法完全準確，語言規範控制優秀",
          good: "字詞、標點和語法大多準確，語言規範控制良好",
          fair: "字詞、標點和語法有部分錯誤，語言規範控制基本",
          needsImprovement: "字詞、標點和語法錯誤頻繁，語言規範控制不佳",
        },
      },
    },
    bands: [
      {
        level: "優秀",
        scoreRange: [34, 40],
        description: "完全回應題目，組織優秀，語言範圍廣泛，完全準確",
      },
      {
        level: "良好",
        scoreRange: [28, 33],
        description: "回應題目，組織良好，語言範圍良好，大多準確",
      },
      {
        level: "尚可",
        scoreRange: [20, 27],
        description: "部分回應題目，組織基本，語言範圍有限，有部分錯誤",
      },
      {
        level: "待改進",
        scoreRange: [0, 19],
        description: "回應有限，組織不佳，語言範圍非常有限，錯誤頻繁",
      },
    ],
    examPreset: "edexcel_gcse",
  },
};

/**
 * Cambridge iGCSE - Cambridge International IGCSE Chinese (0547)
 * Total: 40 points
 */
const cambridgeIgcseRubric: ExamPresetRubric = {
  value: "cambridge_igcse",
  label: "Cambridge iGCSE",
  category: "iGCSE (International)",
  description: "Cambridge International IGCSE 中文 (0547)",
  gradeLevel: "S4",
  essayConfig: {
    dimensions: {
      content: {
        name: "內容與相關性",
        weight: 35,
        maxScore: 14,
        bandDescriptors: {
          excellent:
            "內容完全相關且充分發展，構思深刻，完全回應題目要求，引人入勝且具原創性",
          good: "內容相關且充分發展，構思清晰，回應題目要求，恰當且引人入勝",
          fair: "內容大致相關，構思基本清晰，部分回應題目要求，發展基本",
          needsImprovement:
            "內容相關性有限或不相關，構思簡單或不清楚，發展不佳",
        },
      },
      organization: {
        name: "結構與連貫",
        weight: 25,
        maxScore: 10,
        bandDescriptors: {
          excellent:
            "結構優秀，層次清晰，段落運用有效，連貫手法複雜精緻，高度連貫",
          good: "結構良好，層次清晰，段落運用恰當，連貫手法運用良好，連貫",
          fair: "結構基本，有基本層次，段落運用簡單，連貫手法有限，部分連貫",
          needsImprovement:
            "結構不佳或無清晰層次，段落運用不足，連貫手法很少，缺乏連貫性",
        },
      },
      language: {
        name: "範圍與準確性",
        weight: 30,
        maxScore: 12,
        bandDescriptors: {
          excellent: "詞彙和句式範圍廣泛，語言使用高度準確，表達複雜精緻且自然",
          good: "詞彙和句式範圍良好，語言使用大多準確，表達清晰且自然",
          fair: "詞彙和句式範圍有限，語言使用部分準確，表達基本",
          needsImprovement: "詞彙和句式範圍非常有限，錯誤頻繁，表達不佳",
        },
      },
      mechanics: {
        name: "字詞準確性",
        weight: 10,
        maxScore: 4,
        bandDescriptors: {
          excellent: "字詞形成和使用完全準確，書寫規範控制優秀",
          good: "字詞形成和使用大多準確，書寫規範控制良好",
          fair: "字詞形成和使用有部分錯誤，書寫規範控制基本",
          needsImprovement: "字詞形成和使用錯誤頻繁，書寫規範控制不佳",
        },
      },
    },
    bands: [
      {
        level: "優秀",
        scoreRange: [34, 40],
        description: "內容完全相關，結構優秀，語言範圍廣泛，字詞準確",
      },
      {
        level: "良好",
        scoreRange: [28, 33],
        description: "內容相關，結構良好，語言範圍良好，字詞大多準確",
      },
      {
        level: "尚可",
        scoreRange: [20, 27],
        description: "內容大致相關，結構基本，語言範圍有限，字詞有部分錯誤",
      },
      {
        level: "待改進",
        scoreRange: [0, 19],
        description: "相關性有限，結構不佳，語言範圍非常有限，字詞錯誤頻繁",
      },
    ],
    examPreset: "cambridge_igcse",
  },
};

/**
 * Edexcel iGCSE - Pearson Edexcel International GCSE Chinese
 * Total: 40 points
 */
const edexcelIgcseRubric: ExamPresetRubric = {
  value: "edexcel_igcse",
  label: "Edexcel iGCSE",
  category: "iGCSE (International)",
  description: "Pearson Edexcel International GCSE 中文",
  gradeLevel: "S4",
  essayConfig: {
    dimensions: {
      content: {
        name: "溝通與內容",
        weight: 40,
        maxScore: 16,
        bandDescriptors: {
          excellent:
            "溝通優秀，內容深刻新穎，完全回應題目要求，構思引人入勝且具原創性",
          good: "溝通良好，內容清晰且充分發展，回應題目要求，構思相關且恰當",
          fair: "溝通尚可，內容基本清晰，部分回應題目要求，構思基本",
          needsImprovement: "溝通有限，內容不清楚或不相關，對題目要求回應不佳",
        },
      },
      organization: {
        name: "組織",
        weight: 20,
        maxScore: 8,
        bandDescriptors: {
          excellent: "組織優秀，邏輯清晰，段落運用有效，連貫手法複雜精緻",
          good: "組織良好，邏輯分明，段落運用恰當，連貫手法運用良好",
          fair: "組織基本，有基本邏輯，段落運用簡單，連貫手法有限",
          needsImprovement: "組織不佳或無清晰邏輯，段落運用不足，連貫手法很少",
        },
      },
      language: {
        name: "語言知識",
        weight: 30,
        maxScore: 12,
        bandDescriptors: {
          excellent:
            "詞彙和句式範圍優秀，語言使用高度準確，展現複雜精緻的語言知識",
          good: "詞彙和句式範圍良好，語言使用大多準確，展現良好的語言知識",
          fair: "詞彙和句式範圍有限，語言使用部分準確，展現基本的語言知識",
          needsImprovement:
            "詞彙和句式範圍非常有限，錯誤頻繁，語言知識展現不佳",
        },
      },
      mechanics: {
        name: "準確性",
        weight: 10,
        maxScore: 4,
        bandDescriptors: {
          excellent: "字詞、標點和語法完全準確，語言規範控制優秀",
          good: "字詞、標點和語法大多準確，語言規範控制良好",
          fair: "字詞、標點和語法有部分錯誤，語言規範控制基本",
          needsImprovement: "字詞、標點和語法錯誤頻繁，語言規範控制不佳",
        },
      },
    },
    bands: [
      {
        level: "優秀",
        scoreRange: [34, 40],
        description: "溝通優秀，組織優秀，語言範圍廣泛，完全準確",
      },
      {
        level: "良好",
        scoreRange: [28, 33],
        description: "溝通良好，組織良好，語言範圍良好，大多準確",
      },
      {
        level: "尚可",
        scoreRange: [20, 27],
        description: "溝通尚可，組織基本，語言範圍有限，有部分錯誤",
      },
      {
        level: "待改進",
        scoreRange: [0, 19],
        description: "溝通有限，組織不佳，語言範圍非常有限，錯誤頻繁",
      },
    ],
    examPreset: "edexcel_igcse",
  },
};

/**
 * TSA 小三 - 全港性系統評估 小學三年級 中文寫作
 * Total: 24 points
 */
const tsaP3Rubric: ExamPresetRubric = {
  value: "tsa_p3",
  label: "TSA 小三",
  category: "TSA (香港)",
  description: "全港性系統評估 小學三年級 中文寫作",
  gradeLevel: "P3",
  essayConfig: {
    dimensions: {
      content: {
        name: "內容",
        weight: 40,
        maxScore: 10,
        bandDescriptors: {
          excellent: "內容豐富，能緊扣題目，有具體的描述和感受，表達清晰",
          good: "內容充實，能回應題目，有描述和感受，表達較為清晰",
          fair: "內容尚可，基本回應題目，有簡單的描述，表達尚算清楚",
          needsImprovement: "內容空泛或離題，缺乏描述，表達不清楚",
        },
      },
      organization: {
        name: "結構",
        weight: 25,
        maxScore: 6,
        bandDescriptors: {
          excellent: "結構完整，段落分明，有開頭、正文、結尾，層次清楚",
          good: "結構較為完整，段落較為分明，有基本的開頭、正文、結尾",
          fair: "結構基本完整，段落尚算分明，有簡單的開頭、正文、結尾",
          needsImprovement: "結構不完整，段落不清，缺乏開頭或結尾",
        },
      },
      language: {
        name: "表達",
        weight: 25,
        maxScore: 6,
        bandDescriptors: {
          excellent: "用詞恰當，句子通順，能運用簡單的修辭手法，表達生動",
          good: "用詞較為恰當，句子較為通順，表達較為清楚",
          fair: "用詞基本正確，句子尚算通順，表達尚算清楚",
          needsImprovement: "用詞不當，句子不通順，表達不清楚",
        },
      },
      mechanics: {
        name: "標點字詞",
        weight: 10,
        maxScore: 2,
        bandDescriptors: {
          excellent: "無錯別字，標點符號使用正確，書寫工整",
          good: "錯別字極少（1個），標點符號使用基本正確，書寫較為工整",
          fair: "有少量錯別字（2-3個），標點符號偶有錯誤，書寫尚可",
          needsImprovement: "錯別字較多（4個以上），標點符號使用混亂，書寫潦草",
        },
      },
    },
    bands: [
      {
        level: "優秀",
        scoreRange: [20, 24],
        description: "內容豐富，結構完整，表達清晰，標點字詞正確",
      },
      {
        level: "良好",
        scoreRange: [16, 19],
        description: "內容充實，結構較為完整，表達較為清晰，標點字詞基本正確",
      },
      {
        level: "尚可",
        scoreRange: [12, 15],
        description: "內容尚可，結構基本完整，表達尚算清楚，有少量錯誤",
      },
      {
        level: "待改進",
        scoreRange: [0, 11],
        description: "內容空泛，結構不完整，表達不清楚，錯誤較多",
      },
    ],
    examPreset: "tsa_p3",
  },
};

/**
 * TSA 小六 - 全港性系統評估 小學六年級 中文寫作
 * Total: 36 points
 */
const tsaP6Rubric: ExamPresetRubric = {
  value: "tsa_p6",
  label: "TSA 小六",
  category: "TSA (香港)",
  description: "全港性系統評估 小學六年級 中文寫作",
  gradeLevel: "P6",
  essayConfig: {
    dimensions: {
      content: {
        name: "內容",
        weight: 40,
        maxScore: 15,
        bandDescriptors: {
          excellent:
            "內容豐富充實，能緊扣題目，有具體生動的描述和深刻的感受，見解獨到",
          good: "內容充實，能回應題目，有描述和感受，有一定見解",
          fair: "內容尚可，基本回應題目，有簡單的描述和感受",
          needsImprovement: "內容空泛或離題，缺乏具體描述，表達不清楚",
        },
      },
      organization: {
        name: "結構",
        weight: 25,
        maxScore: 9,
        bandDescriptors: {
          excellent: "結構完整嚴謹，段落分明，層次清楚，過渡自然，首尾呼應",
          good: "結構完整，段落較為分明，層次較為清楚，過渡較為自然",
          fair: "結構基本完整，段落尚算分明，層次尚算清楚",
          needsImprovement: "結構不完整，段落不清，缺乏連貫性",
        },
      },
      language: {
        name: "表達",
        weight: 25,
        maxScore: 9,
        bandDescriptors: {
          excellent: "用詞恰當優美，句式較為多變，能運用修辭手法，表達生動有力",
          good: "用詞恰當，句式較為多變，能運用簡單修辭，表達較為生動",
          fair: "用詞基本正確，句式較為單調，表達尚算清楚",
          needsImprovement: "用詞不當，句式單調，表達不清楚",
        },
      },
      mechanics: {
        name: "標點字詞",
        weight: 10,
        maxScore: 3,
        bandDescriptors: {
          excellent: "無錯別字，標點符號使用正確，書寫工整美觀",
          good: "錯別字極少（1-2個），標點符號使用基本正確，書寫較為工整",
          fair: "有少量錯別字（3-4個），標點符號偶有錯誤，書寫尚可",
          needsImprovement: "錯別字較多（5個以上），標點符號使用混亂，書寫潦草",
        },
      },
    },
    bands: [
      {
        level: "優秀",
        scoreRange: [30, 36],
        description: "內容豐富充實，結構完整嚴謹，表達生動有力，標點字詞正確",
      },
      {
        level: "良好",
        scoreRange: [24, 29],
        description: "內容充實，結構完整，表達較為生動，標點字詞基本正確",
      },
      {
        level: "尚可",
        scoreRange: [18, 23],
        description: "內容尚可，結構基本完整，表達尚算清楚，有少量錯誤",
      },
      {
        level: "待改進",
        scoreRange: [0, 17],
        description: "內容空泛，結構不完整，表達不清楚，錯誤較多",
      },
    ],
    examPreset: "tsa_p6",
  },
};

/**
 * TSA 中三 - 全港性系統評估 中學三年級 中文寫作
 * Total: 42 points
 */
const tsaS3Rubric: ExamPresetRubric = {
  value: "tsa_s3",
  label: "TSA 中三",
  category: "TSA (香港)",
  description: "全港性系統評估 中學三年級 中文寫作",
  gradeLevel: "S3",
  essayConfig: {
    dimensions: {
      content: {
        name: "內容",
        weight: 40,
        maxScore: 17,
        bandDescriptors: {
          excellent:
            "內容豐富充實，能緊扣題目，有具體生動的描述和深刻的感受，見解獨到深刻",
          good: "內容充實，能回應題目，有描述和感受，有一定見解",
          fair: "內容尚可，基本回應題目，有簡單的描述和感受",
          needsImprovement: "內容空泛或離題，缺乏具體描述，表達不清楚",
        },
      },
      organization: {
        name: "結構",
        weight: 25,
        maxScore: 10,
        bandDescriptors: {
          excellent: "結構完整嚴謹，段落分明，層次清楚，過渡自然流暢，首尾呼應",
          good: "結構完整，段落較為分明，層次較為清楚，過渡較為自然",
          fair: "結構基本完整，段落尚算分明，層次尚算清楚",
          needsImprovement: "結構不完整，段落不清，缺乏連貫性，過渡生硬",
        },
      },
      language: {
        name: "表達",
        weight: 25,
        maxScore: 11,
        bandDescriptors: {
          excellent:
            "用詞恰當優美，句式靈活多變，能運用修辭手法，表達生動有力，文采較好",
          good: "用詞恰當，句式較為多變，能運用簡單修辭，表達較為生動",
          fair: "用詞基本正確，句式較為單調，表達尚算清楚",
          needsImprovement: "用詞不當，句式單調，表達不清楚，文筆生硬",
        },
      },
      mechanics: {
        name: "標點字詞",
        weight: 10,
        maxScore: 4,
        bandDescriptors: {
          excellent: "無錯別字，標點符號使用正確，書寫工整美觀",
          good: "錯別字極少（1-2個），標點符號使用基本正確，書寫較為工整",
          fair: "有少量錯別字（3-5個），標點符號偶有錯誤，書寫尚可",
          needsImprovement: "錯別字較多（6個以上），標點符號使用混亂，書寫潦草",
        },
      },
    },
    bands: [
      {
        level: "優秀",
        scoreRange: [35, 42],
        description: "內容豐富充實，結構完整嚴謹，表達生動有力，標點字詞正確",
      },
      {
        level: "良好",
        scoreRange: [28, 34],
        description: "內容充實，結構完整，表達較為生動，標點字詞基本正確",
      },
      {
        level: "尚可",
        scoreRange: [21, 27],
        description: "內容尚可，結構基本完整，表達尚算清楚，有少量錯誤",
      },
      {
        level: "待改進",
        scoreRange: [0, 20],
        description: "內容空泛，結構不完整，表達不清楚，錯誤較多",
      },
    ],
    examPreset: "tsa_s3",
  },
};

/**
 * All exam preset rubrics
 */
export const EXAM_PRESET_RUBRICS: ExamPresetRubric[] = [
  dseChineseRubric,
  aqaGcseRubric,
  edexcelGcseRubric,
  cambridgeIgcseRubric,
  edexcelIgcseRubric,
  tsaP3Rubric,
  tsaP6Rubric,
  tsaS3Rubric,
];

/**
 * Get a preset rubric by value
 */
export function getPresetRubric(value: string): ExamPresetRubric | undefined {
  return EXAM_PRESET_RUBRICS.find((rubric) => rubric.value === value);
}

/**
 * Get all preset rubrics grouped by category
 */
export function getPresetRubricsByCategory(): Record<
  string,
  ExamPresetRubric[]
> {
  return EXAM_PRESET_RUBRICS.reduce((acc, rubric) => {
    if (!acc[rubric.category]) {
      acc[rubric.category] = [];
    }
    const categoryArray = acc[rubric.category];
    if (categoryArray) {
      categoryArray.push(rubric);
    }
    return acc;
  }, {} as Record<string, ExamPresetRubric[]>);
}
